import React, { useMemo, useRef } from 'react';
import { Button, Text } from 'src/@vymo/ui/atoms';
import DotsLoader from 'src/@vymo/ui/atoms/loader/indicator/dots';
import { Form } from 'src/@vymo/ui/molecules';
import { FormVersion } from 'src/@vymo/ui/molecules/form/types';
import { ReactComponent as NoMeeting } from 'src/assets/icons/noMeetings.svg';
import { Keys, locale } from 'src/i18n';
import { setCurrentEntityId, setDetails } from 'src/models/details/slice';
import { fetchWorkflowDetails } from 'src/models/details/thunk';
import { getRevyreConfig } from 'src/models/lmsConfig/selectors';
import { REVYRE_FORM } from 'src/modules/constants';
// eslint-disable-next-line vymo-ui/restrict-import
import { getEntityInputsAndGroups } from 'src/modules/details360/detailsBody/details/queries';
// eslint-disable-next-line vymo-ui/restrict-import
import { getWorkflowCode } from 'src/modules/revyre/form/queries';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { renderDialog } from 'src/workspace/slice';
import { getCalendarActivityDetails, getIsLoading } from '../selectors';
import styles from './index.module.scss';

function RevyreActivityDetails({ entityId }) {
  const formRef = useRef(null);
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(getIsLoading);

  const calendarItemData = useAppSelector(getCalendarActivityDetails);
  const revyreConfig = useAppSelector(getRevyreConfig);
  const detailsWorkflow = calendarItemData?.mutatedEntity?.workflowType;

  const { inputs, groups, formValues } = useMemo(
    () =>
      getEntityInputsAndGroups(
        { revyre: true, domainType: 'activity' },
        calendarItemData
          ? {
              ...calendarItemData,
              inputsMap: calendarItemData?.mutatedEntity,
            }
          : null,
        revyreConfig,
      ),
    [calendarItemData, revyreConfig],
  );

  const workflowEvents = useMemo(
    () =>
      revyreConfig?.[detailsWorkflow]?.events?.map((item) => ({
        ...item,
        code: item?.allowedAction,
        name: item?.actionName,
      })) || [],
    [detailsWorkflow, revyreConfig],
  );

  if (!calendarItemData) {
    return (
      <div className={styles.activityCalendarCard__content__noSelection}>
        <NoMeeting />
        <Text>{locale(Keys.ACTIVITY_CARD_PLACEHOLDER)}</Text>
      </div>
    );
  }

  if (isLoading && !calendarItemData) {
    return (
      <div className={styles.activityCalendarCard__content__noSelection}>
        <DotsLoader width="var(--dimensions-600)" />
        <Text>{locale(Keys.ACTIVITY_CARD_LOADING)}</Text>
      </div>
    );
  }

  const handleOpenRevyreForm = async () => {
    const workflowId = getWorkflowCode(
      calendarItemData?.mutatedEntity?.workflowId,
    );

    dispatch(setCurrentEntityId(entityId));
    dispatch(
      setDetails({
        entityId,
        data: { payload: { ...calendarItemData } },
        revyre: true,
      }),
    );

    const workflowType = calendarItemData?.mutatedEntity?.workflowType;

    if (workflowId && workflowType) {
      fetchWorkflowDetails({
        workflowId,
        domainType: workflowType,
        workflowType,
        dispatch,
      });
    }

    dispatch(
      renderDialog({
        id: REVYRE_FORM,
        props: {
          type: 'update',
          domainType: 'activity',
          activityType: calendarItemData?.mutatedEntity?.activityType,
        },
      }),
    );
  };

  return (
    <div className={styles.activityDetails}>
      {/* HEADER */}
      <div className={styles.activityDetails__header}>
        <Text type="h5">{calendarItemData?.mutatedEntity?.name}</Text>
        {calendarItemData?.mutatedEntity?.currentState?.stateId !==
          'COMPLETED' && (
          <Button
            disabled={!calendarItemData?.allowedActionsAndMetadata?.length}
            type="outlined"
            variant="subtle"
            onClick={handleOpenRevyreForm}
          >
            {locale(Keys.UPDATE)}
          </Button>
        )}
      </div>

      {inputs?.length > 0 ? (
        <div className={styles.activityDetails__content}>
          <Form
            ref={formRef}
            id="activityDetails"
            name="activityDetails"
            onChange={() => {}}
            config={{
              version: FormVersion.webRevyre,
              data: inputs,
              grouping: groups,
              fieldItemConfig: { showDisabledIcon: true },
              customPayload: {
                entityNextEventOptions: workflowEvents,
              },
              viewMode: true,
            }}
            className={styles.activityDetails__form}
            value={formValues}
          />
        </div>
      ) : (
        <div className={styles.activityCalendarCard__content__noSelection}>
          <NoMeeting />
          <Text>{locale(Keys.NO_DATA_FOR_THIS_ACTIVITY)}</Text>
        </div>
      )}
    </div>
  );
}
export default RevyreActivityDetails;
